import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { getArticle, type Article } from "@/lib/articles";
import { getMagazineBySlug } from "@/lib/magazine.functions";
import { ShareButtons } from "@/components/ShareButtons";
import { InlineNewsletter } from "@/components/InlineNewsletter";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { MoreLink } from "@/components/site/page";
import {
  hreflangLinks,
  trPersonSchema,
  trPublisherSchema,
  TR_PERSON_ID,
  TR_PUBLISHER_ID,
} from "@/lib/seo";

export const Route = createFileRoute("/dergi/$slug")({
  loader: async ({ params }) => {
    const staticArticle = await getArticle(params.slug);
    if (staticArticle) return staticArticle;
    const dbRow = await getMagazineBySlug({ data: { slug: params.slug } });
    if (!dbRow) throw notFound();
    const article: Article = {
      slug: dbRow.slug,
      magazine: dbRow.magazine,
      date: dbRow.display_date,
      isoDate: dbRow.iso_date,
      title: dbRow.title,
      dek: dbRow.dek,
      excerpt: dbRow.excerpt,
      body: dbRow.body,
    };
    return article;
  },
  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [] };
    const url = `https://akillisurdurulebilirlik.com/dergi/${loaderData.slug}`;
    return {
      meta: [
        { title: `${loaderData.title} — Arda Öztaşkın` },
        { name: "description", content: loaderData.dek },
        { property: "og:title", content: loaderData.title },
        { property: "og:description", content: loaderData.dek },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { property: "og:locale", content: "tr_TR" },
        { property: "article:published_time", content: loaderData.isoDate },
        { property: "article:author", content: "Arda Öztaşkın" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/dergi/${loaderData.slug}`),
      ],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(trPersonSchema()) },
        { type: "application/ld+json", children: JSON.stringify(trPublisherSchema()) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: loaderData.title,
            description: loaderData.dek,
            datePublished: loaderData.isoDate,
            dateModified: loaderData.isoDate,
            image: ["https://akillisurdurulebilirlik.com/og-image.jpg"],
            author: { "@id": TR_PERSON_ID },
            publisher: { "@id": TR_PUBLISHER_ID },
            mainEntityOfPage: { "@type": "WebPage", "@id": url },
            inLanguage: "tr-TR",
            articleSection: loaderData.magazine,
            isPartOf: loaderData.magazine,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Ana sayfa", item: "https://akillisurdurulebilirlik.com/" },
              { "@type": "ListItem", position: 2, name: "Dergi", item: "https://akillisurdurulebilirlik.com/dergi" },
              { "@type": "ListItem", position: 3, name: loaderData.title, item: url },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-5 py-24 text-center sm:px-8">
      <h1 className="mb-4 font-serif text-3xl">Yazı bulunamadı</h1>
      <MoreLink to="/">Ana sayfaya dön</MoreLink>
    </div>
  ),
  errorComponent: ({ error }) => {
    if (typeof console !== "undefined") console.error(error);
    return (
      <div className="mx-auto max-w-3xl px-5 py-24 text-center sm:px-8">
        <h1 className="mb-4 font-serif text-3xl">Bir şeyler ters gitti</h1>
        <p className="mb-6 text-sm text-ink/70">Yükleme sırasında bir sorun oluştu.</p>
        <MoreLink to="/">Ana sayfa</MoreLink>
      </div>
    );
  },
  component: ArticlePage,
});

function ArticlePage() {
  const article = Route.useLoaderData();
  return (
    <>
      <div className="mx-auto max-w-7xl px-5 pt-5 sm:px-8 lg:pt-7">
        <Breadcrumbs items={[{ label: "Dergi", to: "/dergi" }, { label: article.title }]} />
      </div>
      <article className="mx-auto max-w-3xl px-5 pb-16 pt-6 sm:px-8 md:pb-24 lg:pt-10">
        <header className="mb-10 border-b border-ink/10 pb-10">
          <p className="mb-5 text-[10px] uppercase tracking-[0.3em] text-ink/70">
            {article.magazine} · {article.date}
          </p>
          <h1 className="mb-5 text-balance font-serif text-4xl font-medium leading-[1.05] tracking-tight md:text-5xl">
            {article.title}
          </h1>
          <p className="text-pretty font-serif text-lg italic leading-relaxed text-ink/70 md:text-xl">
            {article.dek}
          </p>
          <div className="mt-8">
            <ShareButtons path={`/dergi/${article.slug}`} title={article.title} excerpt={article.dek} size="md" />
          </div>
        </header>

        <div className="space-y-6 leading-relaxed text-ink/85">
          {article.body.map((block: Article["body"][number], i: number) => {
            if (block.type === "h2") {
              return (
                <h2 key={i} className="mt-12 mb-2 font-serif text-2xl tracking-tight md:text-3xl">
                  {block.text}
                </h2>
              );
            }
            if (block.type === "h3") {
              return (
                <h3
                  key={i}
                  className={`mt-10 mb-1 font-serif text-xl tracking-tight md:text-2xl ${block.italic ? "italic" : ""}`}
                >
                  {block.text}
                </h3>
              );
            }
            if (block.type === "blockquote") {
              return (
                <blockquote
                  key={i}
                  className="my-10 border-l-2 border-clay pl-6 font-serif text-xl italic text-ink/80 md:text-2xl"
                >
                  {block.text}
                </blockquote>
              );
            }
            if (block.type === "hr") {
              return <hr key={i} className="my-10 border-ink/15" />;
            }
            return (
              <p key={i} className="text-base md:text-lg">
                {block.text}
              </p>
            );
          })}
        </div>

        <footer className="mt-16 flex flex-col gap-6 border-t border-ink/10 pt-10 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs uppercase tracking-[0.2em] text-ink/60">
            Yazar: <span className="text-ink">Arda Öztaşkın</span>
          </p>
          <MoreLink to="/dergi">Tüm dergi yazıları</MoreLink>
        </footer>
        <InlineNewsletter />
      </article>
    </>
  );
}
